import { Async, toAsyncFetched } from '@client/new/helpers/types';
import {
  useAdminUpdateInfluencerMutation,
  useGetInfluencersListQuery,
} from 'generated/graphql';
import ajax from '@client/components/ajax';
import { useMemo } from 'react';
import { toInfluencers } from './InfluencerList.mappers';
import { Influencer, InfluencerListModel } from './InfluencerList.types';

export const useInfluencerListModel = () => {
  const query = useGetInfluencersListQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [updateTierMutation] = useAdminUpdateInfluencerMutation();

  const data = useMemo((): Async<Influencer[]> => {
    if (query.data?.creators) {
      try {
        return toAsyncFetched(toInfluencers(query.data.creators));
      } catch (err) {
        return {
          type: 'fetch-error',
          value: (err as Error)?.message ?? 'Cannot map model',
        };
      }
    }
    if (query.error) return { type: 'fetch-error', value: query.error.message };
    if (query.loading) return { type: 'fetching' };
    return { type: 'not-fetched' };
  }, [query.data, query.loading, query.error]);

  if (data.type !== 'fetched') return data;

  const updateTier: InfluencerListModel['updateTier'] = async ({
    influencerId,
    tier,
  }) => {
    await updateTierMutation({
      variables: {
        data: {
          id: influencerId,
          tier,
        },
      },
    });
  };

  const deleteInfluencer: InfluencerListModel['deleteInfluencer'] = async (
    influencerId,
  ) => {
    await ajax.deleteInfluencer(influencerId);
    await query.refetch();
  };

  const refetch: InfluencerListModel['refetch'] = async () => {
    await query.refetch();
  };

  return toAsyncFetched<InfluencerListModel>({
    influencers: data.value,
    updateTier,
    deleteInfluencer,
    refetch,
  });
};
